/**
 * The choosing rows of the configurator: product and evaluation, account size (chips with their price, or a table of
 * every size), challenge type cards and the model switch. Nothing scrolls sideways or is cut on a phone (owner,
 * 2026-10-31): products and challenge types stack as compact rows, the sizes wrap three to a row, the evaluation is a
 * segmented control across the full width — every choice visible at 360px.
 */
import { cn } from "@prop/ui";
import { useQueries } from "@tanstack/react-query";
import { LayoutGrid, Rows3 } from "lucide-react";
import type { ReactNode } from "react";
import { useConfigT } from "../../../i18n";
import { post } from "../../../lib/api";
import { fmtUsd } from "../../../lib/fmt";
import { type CatalogProgram, type Choices, axisOf, type Family, nearest, priceOf, resolveChoices, rulesOf, sizeLabel, sizeOf, taglineOf } from "./model";
import { Glide, Swap } from "./motion";
import type { Config } from "./useConfigurator";


/** A group of choices with its label beside it on wide screens, above it on a phone (a label, not an eyebrow). */
export function Field({ label, aside, children }: { label: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium">{label}</span>
        {aside}
      </div>
      {children}
    </div>
  );
}

/** A choice: the ink fill of the chosen one is a <Glide> that slides between choices; its content sits above it. */
const choice = (on: boolean) =>
  cn(
    "relative isolate min-w-0 rounded-[12px] text-left transition-[color,border-color,transform,translate,scale,box-shadow] duration-200 hover:-translate-y-0.5 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&>:not(.glide)]:relative [&>:not(.glide)]:z-10",
    on ? "border border-transparent text-background" : "border border-foreground/15 bg-foreground/[0.05] text-foreground hover:border-foreground/45 hover:bg-foreground/[0.08] hover:shadow-[0_10px_28px_-16px_rgba(0,0,0,0.45)]",
  );

export function ProductRow({ c }: { c: Config }) {
  const t = useConfigT();
  return (
    <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label={t("product.label")}>
      {c.products.map((p) => {
        const sample = c.group[0]?.sizes[0];
        const count = p === c.product ? sample?.instrumentCount : null;
        return (
          <button key={p} type="button" role="radio" aria-checked={c.product === p} onClick={() => c.setProduct(p)} className={cn(choice(c.product === p), "flex min-h-12 items-center justify-between gap-x-3 px-3.5 py-2.5 sm:flex-col sm:items-start sm:justify-start sm:gap-0.5")}>
            {c.product === p && <Glide group="product" className="glide rounded-[inherit]" />}
            <span className="font-display text-[15px] font-semibold uppercase leading-tight tracking-tight [font-stretch:120%]">{t(`product.${p}`)}</span>
            <span className={cn("text-right text-xs sm:text-left", c.product === p ? "opacity-75" : "text-muted-foreground")}>{count ? t("instruments", { n: count }) : t(`productNote.${p}`)}</span>
          </button>
        );
      })}
    </div>
  );
}

export function EvaluationRow({ c }: { c: Config }) {
  const t = useConfigT();
  return (
    <div className="@container min-w-0" role="radiogroup" aria-label={t("evaluation.label")}>
      <div className="grid grid-cols-2 gap-1 rounded-[22px] bg-muted p-1 @min-[300px]:grid-flow-col @min-[300px]:grid-cols-none @min-[300px]:auto-cols-fr @min-[300px]:rounded-full sm:inline-flex">
        {c.evaluations.map((e) => (
          <button
            key={e}
            type="button"
            role="radio"
            aria-checked={c.evaluation === e}
            onClick={() => c.setEvaluation(e)}
            className={cn(
              "relative isolate min-h-10 min-w-0 rounded-full px-2 text-[13px] font-medium sm:px-4 sm:text-sm transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              c.evaluation === e ? "text-background" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {c.evaluation === e && <Glide group="evaluation" className="glide rounded-full" />}
            <span className="relative z-10">{t(`evaluation.${e}`)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ViewToggle({ c }: { c: Config }) {
  const t = useConfigT();
  return (
    <fieldset className="flex shrink-0 items-center gap-0.5 rounded-full border border-border p-0.5" aria-label={t("size.view")}>
      {(["cards", "table"] as const).map((v) => {
        const Icon = v === "cards" ? LayoutGrid : Rows3;
        return (
          <button
            key={v}
            type="button"
            aria-pressed={c.view === v}
            title={t(`size.${v}`)}
            onClick={() => c.setView(v)}
            className={cn("inline-flex size-9 items-center justify-center rounded-full transition-colors duration-200", c.view === v ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground")}
          >
            <Icon className="size-4" aria-hidden />
            <span className="sr-only">{t(`size.${v}`)}</span>
          </button>
        );
      })}
    </fieldset>
  );
}

export function SizeField({ c }: { c: Config }) {
  const t = useConfigT();
  const sizes = c.family?.sizes ?? [];
  const first = sizes[0];
  const last = sizes.at(-1);
  const range = first && last ? `${sizeLabel(sizeOf(first))} – ${sizeLabel(sizeOf(last))}` : "";
  return (
    <Field
      label={t("size.label")}
      aside={
        <span className="flex items-center gap-3">
          <span className="text-sm tabular-nums text-muted-foreground">{range}</span>
          <span className="hidden sm:flex">
            <ViewToggle c={c} />
          </span>
        </span>
      }
    >
      {/* The table needs a wide panel: a phone always gets the cards. */}
      <div className={c.view === "cards" ? undefined : "sm:hidden"}>
        <div className="@container min-w-0">
        <div className="grid grid-cols-3 gap-x-2 gap-y-3.5 pt-2 @min-[440px]:grid-cols-4 @min-[560px]:grid-cols-5 @min-[700px]:grid-flow-col @min-[700px]:grid-cols-none @min-[700px]:auto-cols-fr" role="radiogroup" aria-label={t("size.label")}>
          {sizes.map((p) => {
            const on = sizeOf(p) === c.size;
            const pr = priceOf(p, resolveChoices(p, c.prefs));
            return (
              <button key={p.slug} type="button" role="radio" aria-checked={on} onClick={() => c.setSize(sizeOf(p))} className={cn(choice(on), "flex flex-col items-start gap-0.5 px-2.5 pb-2 pt-2.5")}>
                {on && <Glide group="size" className="glide rounded-[inherit]" />}
                {sizeOf(p) === c.recommended && (
                  <span className={cn("cfg-badge !absolute -top-2 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-full px-1.5 py-px text-[11px] font-semibold leading-4", on ? "bg-[#d4ff24] text-[#030407]" : "bg-foreground text-background")}>{t("size.recommended")}</span>
                )}
                <span className="text-base font-semibold tabular-nums">{sizeLabel(sizeOf(p))}</span>
                <Swap value={pr.priceUsd ?? "—"} className={cn("text-xs tabular-nums", on ? "opacity-75" : "text-muted-foreground")}>
                  {pr.priceUsd ? fmtUsd(pr.priceUsd) : "—"}
                </Swap>
              </button>
            );
          })}
        </div>
        </div>
      </div>
      {c.view === "table" && (
        <div className="hidden sm:block">
          <SizeTable c={c} sizes={sizes} />
        </div>
      )}
    </Field>
  );
}

type Quote = { rules: CatalogProgram["rules"]; priceUsd: string | null; activationFeeUsd: string | null };
const pct = (a: { pct: string } | { usd: string } | undefined) => (!a ? "—" : "pct" in a ? `${a.pct} %` : fmtUsd(a.usd));

/** Every size of the type × its key rules, for the buyer's reward share and model (quoted when not the default). */
function SizeTable({ c, sizes }: { c: Config; sizes: CatalogProgram[] }) {
  const t = useConfigT();
  const quotes = useQueries({
    queries: sizes.map((p) => {
      const ch = resolveChoices(p, c.prefs);
      const isDefault = Object.entries(ch).every(([k, v]) => p.defaultChoices?.[k] === v);
      return {
        queryKey: ["quote", p.versionId, JSON.stringify(ch)],
        queryFn: () => post<Quote>("/programs/quote", { versionId: p.versionId, options: ch }),
        enabled: !isDefault,
        staleTime: Number.POSITIVE_INFINITY,
      };
    }),
  });
  const hasFee = sizes.some((p) => priceOf(p, resolveChoices(p, c.prefs)).activationFeeUsd);
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] text-sm">
        <thead className="text-left text-muted-foreground">
          <tr className="border-b border-border">
            <th className="py-2 pr-3 font-normal">{t("table.size")}</th>
            <th className="px-3 py-2 text-right font-normal">{t("table.price")}</th>
            {hasFee && <th className="px-3 py-2 text-right font-normal">{t("table.fee")}</th>}
            <th className="px-3 py-2 text-right font-normal">{t("table.target")}</th>
            <th className="px-3 py-2 text-right font-normal">{t("table.daily")}</th>
            <th className="px-3 py-2 text-right font-normal">{t("table.max")}</th>
            <th className="py-2 pl-3 text-right font-normal">{t("table.split")}</th>
          </tr>
        </thead>
        <tbody>
          {sizes.map((p, i) => {
            const ch: Choices = resolveChoices(p, c.prefs);
            const r = quotes[i]?.data?.rules ?? rulesOf(p);
            const pr = priceOf(p, ch);
            const ph = r.phases[0];
            const on = sizeOf(p) === c.size;
            return (
              <tr key={p.slug} className={cn("border-b border-border transition-colors duration-150 last:border-0", on ? "bg-muted/70" : "hover:bg-muted/40")}>
                <td className="py-0 pr-3">
                  <button type="button" aria-pressed={on} onClick={() => c.setSize(sizeOf(p))} className="min-h-11 w-full text-left font-semibold tabular-nums underline-offset-4 hover:underline">
                    {sizeLabel(sizeOf(p))}
                    <span className="sr-only">{t("table.pick", { size: sizeLabel(sizeOf(p)) })}</span>
                  </button>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{pr.priceUsd ? fmtUsd(pr.priceUsd) : "—"}</td>
                {hasFee && <td className="px-3 py-2 text-right tabular-nums">{pr.activationFeeUsd ? fmtUsd(pr.activationFeeUsd) : "—"}</td>}
                <td className="px-3 py-2 text-right tabular-nums">{r.phases.filter((x) => x.profitTarget).map((x) => pct(x.profitTarget)).join(" · ") || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{pct(ph?.dailyLoss?.amount)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{pct(ph?.maxLoss.amount)}</td>
                <td className="py-2 pl-3 text-right tabular-nums">{r.payouts ? `${r.payouts.profitSplitPct} %` : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function TypeCards({ c }: { c: Config }) {
  const t = useConfigT();
  return (
    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3" role="radiogroup" aria-label={t("type.label")}>
      {c.group.map((f: Family) => {
        const on = f.family === c.family?.family;
        const p = nearest(f, c.size);
        const pr = priceOf(p, resolveChoices(p, c.prefs));
        const tag = taglineOf(c.group, f, c.size, c.prefs);
        return (
          <button key={f.family} type="button" role="radio" aria-checked={on} onClick={() => c.setFamily(f)} className={cn(choice(on), "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 px-3.5 py-3 sm:flex sm:flex-col sm:items-start sm:gap-1.5")}>
            {on && <Glide group="type" className="glide rounded-[inherit]" />}
            <span className="font-display text-base font-semibold uppercase leading-tight tracking-tight [font-stretch:120%] sm:leading-none">{f.name}</span>
            <span className={cn("col-start-1 text-sm sm:min-h-5", on ? "opacity-80" : "text-muted-foreground")}>{tag ? t(`tag.${tag.key}`, { h: tag.hours ?? 0 }) : " "}</span>
            <span className="col-start-2 row-span-2 row-start-1 flex flex-col items-end tabular-nums sm:mt-auto sm:flex-row sm:items-baseline sm:gap-2">
              <Swap value={pr.priceUsd ?? "—"} className="text-[15px] font-semibold">
                {pr.priceUsd ? fmtUsd(pr.priceUsd) : "—"}
              </Swap>
              <span className={cn("text-xs", on ? "opacity-75" : "text-muted-foreground")}>{sizeLabel(sizeOf(p))}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Classic / Turbo (futures: no activation fee / Standard), when the chosen size sells both. */
export function ModelSwitch({ c }: { c: Config }) {
  const t = useConfigT();
  const p = c.program;
  const axis = axisOf(p, "model");
  if (!p || !axis) return null;
  const fut = c.product === "futures";
  return (
    <Field label={t("model.label")}>
      <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t("model.label")}>
        {axis.values.map(({ value }) => {
          const on = c.choices.model === value;
          const pr = priceOf(p, { ...c.choices, model: value });
          const name = value === "turbo" ? t(fut ? "model.futTurbo" : "model.turbo") : t(fut ? "model.futClassic" : "model.classic");
          return (
            <button key={value} type="button" role="radio" aria-checked={on} onClick={() => c.setPref("model", value)} className={cn(choice(on), "flex flex-col gap-0.5 px-3.5 py-2.5")}>
              {on && <Glide group="model" className="glide rounded-[inherit]" />}
              <span className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className="font-semibold">{name}</span>
                <Swap value={pr.priceUsd ?? "—"} className="text-sm font-semibold tabular-nums">
                  {pr.priceUsd ? fmtUsd(pr.priceUsd) : "—"}
                </Swap>
              </span>
              <span className={cn("text-xs", on ? "opacity-75" : "text-muted-foreground")}>
                {pr.activationFeeUsd ? t("model.plusFee", { fee: fmtUsd(pr.activationFeeUsd) }) : t("model.classicNote")}
              </span>
            </button>
          );
        })}
      </div>
    </Field>
  );
}
